import { Badge, Box, Button, Flex, Group, Text } from '@mantine/core';
import { IconFilePencil, IconFilePlus, IconFileX, IconStar } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { TemplateProposedAction } from '../chat/types';
import { ProposalDecidedLine } from './ProposalDecidedLine';

type Props = {
  proposal: TemplateProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

const OPERATION_ICONS = {
  CREATE: IconFilePlus,
  UPDATE: IconFilePencil,
  DELETE: IconFileX,
  SET_DEFAULT: IconStar,
} as const;

const OPERATION_NOUNS = {
  CREATE: 'creation',
  UPDATE: 'update',
  DELETE: 'deletion',
  SET_DEFAULT: 'default change',
} as const;

const TYPE_LABELS = {
  MEETING_NOTE: 'Meeting note template',
  MEETING_AGENDA: 'Meeting agenda template',
} as const;

type StatBadge = { label: string; color: string; variant: 'light' | 'default' };

function changeStatBadges(proposal: TemplateProposedAction): StatBadge[] {
  const stats = proposal.changeStats;
  if (!stats) return [];
  const { sectionsAdded = 0, sectionsModified = 0, sectionsRemoved = 0 } = stats;
  const badges: StatBadge[] = [];
  if (sectionsAdded > 0) {
    badges.push({
      label: `${sectionsAdded} section${sectionsAdded === 1 ? '' : 's'} added`,
      color: 'success',
      variant: 'light',
    });
  }
  if (sectionsModified > 0) {
    badges.push({
      label: `${sectionsModified} section${sectionsModified === 1 ? '' : 's'} modified`,
      color: 'orange',
      variant: 'light',
    });
  }
  if (sectionsRemoved > 0) {
    badges.push({
      label: `${sectionsRemoved} section${sectionsRemoved === 1 ? '' : 's'} removed`,
      color: 'error',
      variant: 'light',
    });
  }
  if (stats.previousDefaultName) {
    badges.push({
      label: `Replaces ${stats.previousDefaultName}`,
      color: 'gray',
      variant: 'default',
    });
  }
  return badges;
}

export function TemplateProposalWidget({ proposal, onDecide, disabled }: Props) {
  const OperationIcon = OPERATION_ICONS[proposal.operation];
  const badges = changeStatBadges(proposal);
  const isPending = proposal.status === 'PENDING' || !proposal.status;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <OperationIcon size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Box>
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
              {proposal.templateName}
            </Text>
            <Text size="textXs" c="gray.7">
              {TYPE_LABELS[proposal.templateType]} · proposed {OPERATION_NOUNS[proposal.operation]}
            </Text>
          </Box>
        </Group>
        <Text size="textSm" c="gray.8">
          {proposal.summary}
        </Text>
        {badges.length > 0 && (
          <Group gap="xxs">
            {badges.map(badge => (
              <Badge
                key={badge.label}
                variant={badge.variant}
                color={badge.color}
                size="sm"
                tt="none"
              >
                {badge.label}
              </Badge>
            ))}
          </Group>
        )}
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <ProposalDecidedLine status={proposal.status} failureReason={proposal.failureReason} />
        )}
      </Flex>
    </Box>
  );
}
